import React from 'react';
import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { getWorkspaceTokens } from './workspaceStyles';
import AccessTierBadge from '../AccessTierBadge';

function WorkspaceSidebar({
  title,
  description,
  badge,
  icon: TitleIcon,
  items = [],
  activeSectionId,
  activeViewId,
  activeNavigationItemId,
  activeNavigationLevelId,
  activeNavigationViewId,
  activeItemAction = null,
  headerActions = null,
  actions = null,
  topInset = 0,
  headerMinHeight = 0,
  collapsed = false,
  expandActiveSectionOnly = false,
  onToggleCollapsed,
  onNavigate,
}) {
  const theme = useTheme();
  const tokens = getWorkspaceTokens(theme);
  const sidebarWidth = collapsed ? 62 : 278;
  const isGrouped = items.some((item) => Array.isArray(item.items));

  const renderViewButton = ({ item, view, level }) => {
    const ViewIcon = view.icon;
    const isActive = (
      view.isActive
      || activeNavigationViewId === view.id
      || (level && activeNavigationLevelId === level.id && activeNavigationViewId === view.id)
      || (!activeNavigationViewId && activeSectionId === item.id && activeViewId === view.id)
    );
    const tooltipTitle = `${item.label} ${level?.label ? `${level.label} ` : ''}${view.label}${view.description ? ` - ${view.description}` : ''}`;
    const accessPolicyId = String(view.badge || '').toLowerCase() === 'preview'
      ? null
      : view.accessPolicyId;

    return (
      <Tooltip key={`${item.id}-${level?.id || 'view'}-${view.id}`} title={collapsed ? tooltipTitle : ''} placement="right" disableHoverListener={!collapsed}>
        <Button
          component={RouterLink}
          to={view.to}
          onClick={onNavigate}
          fullWidth
          startIcon={ViewIcon ? <ViewIcon sx={{ fontSize: 15 }} /> : undefined}
          sx={{
            justifyContent: collapsed ? 'center' : 'flex-start',
            minHeight: collapsed ? 28 : 30,
            minWidth: 0,
            py: 0.35,
            px: collapsed ? 0 : 1,
            mb: 0.2,
            borderRadius: collapsed ? '999px' : `${tokens.radius}px`,
            textTransform: 'none',
            fontSize: 12,
            fontWeight: isActive ? 800 : 500,
            color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
            backgroundColor: isActive ? tokens.selected : 'transparent',
            boxShadow: collapsed && isActive
              ? `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`
              : 'none',
            '& .MuiButton-startIcon': { mr: collapsed ? 0 : 0.75, ml: 0 },
            '& .MuiButton-startIcon svg': { fontSize: collapsed ? '13px !important' : '15px !important' },
            '& .view-description': {
              maxHeight: 0,
              opacity: 0,
              transform: 'translateY(-2px)',
              overflow: 'hidden',
              transition: 'max-height 140ms ease, opacity 120ms ease, transform 120ms ease',
            },
            '&:hover': {
              backgroundColor: tokens.hover,
              color: theme.palette.primary.main,
            },
            '&:hover .view-description': {
              maxHeight: 24,
              opacity: 1,
              transform: 'translateY(0)',
            },
          }}
        >
          {!collapsed && (
            <Box sx={{ minWidth: 0, textAlign: 'left', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: isActive ? 800 : 600, lineHeight: 1.15 }}>
                  {view.label}
                </Typography>
                {view.badge && <Chip label={view.badge} size="small" sx={{ height: 16, fontSize: 8 }} />}
                {accessPolicyId && (
                  <AccessTierBadge accessPolicyId={accessPolicyId} compact sx={{ ml: 'auto' }} />
                )}
              </Box>
              {view.description && (
                <Typography className="view-description" sx={{ mt: 0.2, fontSize: 10.25, lineHeight: 1.2, color: theme.palette.text.secondary, whiteSpace: 'normal' }}>
                  {view.description}
                </Typography>
              )}
            </Box>
          )}
        </Button>
      </Tooltip>
    );
  };

  const renderLevel = (item, level) => {
    const LevelIcon = level.icon;
    const isActiveLevel = activeNavigationLevelId === level.id;
    const shouldRenderLevelViews = true;
    const accessPolicyId = String(level.badge || '').toLowerCase() === 'preview'
      ? null
      : level.accessPolicyId;

    return (
      <Box key={`${item.id}-${level.id}`} sx={{ mb: collapsed ? 0.25 : 0.65 }}>
        <Tooltip title={collapsed ? `${item.label} ${level.label}` : ''} placement="right" disableHoverListener={!collapsed}>
          <Button
            component={RouterLink}
            to={level.to}
            onClick={onNavigate}
            fullWidth
            startIcon={LevelIcon ? <LevelIcon sx={{ fontSize: 15 }} /> : undefined}
            sx={{
              justifyContent: collapsed ? 'center' : 'flex-start',
              minHeight: collapsed ? 30 : 32,
              minWidth: 0,
              py: 0.4,
              px: collapsed ? 0 : 1,
              borderRadius: `${tokens.radius}px`,
              textTransform: 'none',
              color: isActiveLevel ? theme.palette.primary.main : theme.palette.text.primary,
              backgroundColor: isActiveLevel ? tokens.selected : 'transparent',
              '& .MuiButton-startIcon': { mr: collapsed ? 0 : 0.75, ml: 0 },
              '& .MuiButton-startIcon svg': { fontSize: collapsed ? '14px !important' : '15px !important' },
              '&:hover': {
                backgroundColor: tokens.hover,
                color: theme.palette.primary.main,
              },
            }}
          >
            {!collapsed && (
              <Box sx={{ minWidth: 0, textAlign: 'left', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: isActiveLevel ? 800 : 700, lineHeight: 1.15 }}>
                    {level.label}
                  </Typography>
                  {accessPolicyId && (
                    <AccessTierBadge accessPolicyId={accessPolicyId} compact sx={{ ml: 'auto' }} />
                  )}
                </Box>
                {level.description && (
                  <Typography sx={{ mt: 0.15, fontSize: 10.25, lineHeight: 1.2, color: theme.palette.text.secondary }}>
                    {level.description}
                  </Typography>
                )}
              </Box>
            )}
          </Button>
        </Tooltip>
        {shouldRenderLevelViews && level.views?.length > 0 && (
          <Box sx={{ mt: collapsed ? 0.25 : 0.25, ml: collapsed ? 0 : 2.25, borderLeft: collapsed ? 0 : `1px solid ${theme.palette.divider}`, pl: collapsed ? 0 : 0.75 }}>
            {level.views.map((view) => renderViewButton({ item, level, view }))}
          </Box>
        )}
      </Box>
    );
  };

  const renderNavigationItem = (item) => {
    const SectionIcon = item.icon;
    const isSectionActive = activeNavigationItemId
      ? activeNavigationItemId === item.id
      : activeSectionId === item.id;
    const shouldExpandOnlyActive = isGrouped || expandActiveSectionOnly;
    const shouldRenderLevels = (
      item.levels?.length > 0
      && (!shouldExpandOnlyActive || isSectionActive)
    );
    const shouldRenderViews = (
      !shouldRenderLevels
      && item.views?.length > 0
      && (!shouldExpandOnlyActive || isSectionActive)
    );
    const hasIntegratedAction = isSectionActive && activeItemAction && !collapsed;

    return (
      <Box key={item.id} sx={{ mb: collapsed ? 0.5 : 1 }}>
        <Box
          data-workspace-integrated-action={hasIntegratedAction ? 'true' : undefined}
          sx={{
            minWidth: 0,
            ...(hasIntegratedAction ? {
              borderRadius: `${tokens.radius}px`,
              overflow: 'hidden',
              backgroundColor: tokens.header,
              boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.18)}`,
            } : {}),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <Tooltip title={collapsed ? item.label : ''} placement="right" disableHoverListener={!collapsed}>
            <Button
              component={RouterLink}
              to={item.to}
              onClick={onNavigate}
              fullWidth
              sx={{
                display: 'flex',
                flex: '1 1 auto',
                minWidth: 0,
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: isGrouped ? 1.25 : 1,
                px: collapsed ? 0 : isGrouped ? 1.25 : 1,
                py: isGrouped && !collapsed ? 1.15 : 0.75,
                minHeight: collapsed ? 38 : isGrouped ? 56 : 34,
                borderRadius: hasIntegratedAction ? 0 : `${tokens.radius}px`,
                textTransform: 'none',
                color: theme.palette.text.primary,
                backgroundColor: hasIntegratedAction
                  ? 'transparent'
                  : isSectionActive
                  ? tokens.header
                  : isGrouped
                    ? tokens.header
                    : 'transparent',
                overflow: 'hidden',
                boxShadow: hasIntegratedAction
                  ? 'none'
                  : collapsed
                  ? `inset 0 0 0 1px ${theme.palette.divider}`
                  : isSectionActive
                    ? `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.18)}`
                    : isGrouped
                      ? `inset 0 0 0 1px ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.1 : 0.16)}`
                      : 'none',
                '&:hover': {
                  backgroundColor: tokens.hover,
                  boxShadow: hasIntegratedAction
                    ? 'none'
                    : collapsed
                    ? `inset 0 0 0 1px ${theme.palette.divider}`
                    : `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`,
                },
              }}
            >
              {SectionIcon && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: isGrouped && !collapsed ? 34 : 'auto',
                    height: isGrouped && !collapsed ? 34 : 'auto',
                    borderRadius: `${tokens.radius}px`,
                    color: isGrouped && !collapsed ? theme.palette.primary.main : isSectionActive ? theme.palette.primary.main : theme.palette.text.secondary,
                    backgroundColor: isGrouped && !collapsed ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.22) : 'transparent',
                    flexShrink: 0,
                  }}
                >
                  <SectionIcon sx={{ fontSize: collapsed ? 19 : isGrouped ? 18 : 17 }} />
                </Box>
              )}
              {!collapsed && (
                <Box sx={{ minWidth: 0, textAlign: 'left', flex: '1 1 auto' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: isSectionActive ? theme.palette.primary.main : theme.palette.text.primary, minWidth: 0 }}>
                      {item.label}
                    </Typography>
                    {item.badge && (
                      <Chip
                        label={item.badge}
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: 8,
                          fontWeight: 800,
                          color: theme.palette.text.secondary,
                          borderColor: theme.palette.divider,
                          backgroundColor: tokens.header,
                        }}
                      />
                    )}
                    {item.accessPolicyId
                      && item.showAccessBadge !== false
                      && String(item.badge || '').toLowerCase() !== 'preview' && (
                      <AccessTierBadge
                        accessPolicyId={item.accessPolicyId}
                        compact
                        sx={{ ml: 'auto' }}
                      />
                    )}
                  </Box>
                  {item.description && (
                    <Typography className="section-description" sx={{ mt: 0.15, fontSize: 10.5, color: theme.palette.text.secondary, lineHeight: 1.2 }}>
                      {item.description}
                    </Typography>
                  )}
                </Box>
              )}
            </Button>
          </Tooltip>
          </Box>

        {hasIntegratedAction && (
          <Box
            data-testid="workspace-sidebar-active-item-action"
            sx={{
              px: 0.75,
              py: 0.4,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            {activeItemAction}
          </Box>
        )}
        </Box>

        {shouldRenderLevels && (
          <Box sx={{ mt: collapsed ? 0.4 : 0.55, ml: collapsed ? 0 : 1, pl: collapsed ? 0 : 0.75 }}>
            {item.levels.map((level) => renderLevel(item, level))}
          </Box>
        )}

        {shouldRenderViews && (
          <Box sx={{ mt: collapsed ? 0.4 : 0.35, ml: collapsed ? 0 : 2.25, borderLeft: collapsed ? 0 : `1px solid ${theme.palette.divider}`, pl: collapsed ? 0 : 0.75 }}>
            {item.views.map((view) => renderViewButton({ item, view }))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      data-testid="workspace-sidebar"
      sx={{
        width: sidebarWidth,
        minWidth: sidebarWidth,
        position: 'relative',
        alignSelf: 'stretch',
        minHeight: 'calc(100svh - 48px)',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        pt: topInset ? `${topInset}px` : 0,
        backgroundColor: tokens.rail,
        borderRight: `1px solid ${theme.palette.divider}`,
        transition: 'width 160ms ease, min-width 160ms ease',
      }}
    >
      <Box
        data-testid="workspace-sidebar-header"
        sx={{
          px: collapsed ? 0.5 : 2,
          py: 1.25,
          minHeight: headerMinHeight || 'auto',
          boxSizing: 'border-box',
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: tokens.header,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, justifyContent: collapsed ? 'center' : 'flex-start' }}>
            {TitleIcon && (
              <Tooltip title={collapsed ? title : ''} disableHoverListener={!collapsed}>
                <TitleIcon sx={{ color: theme.palette.primary.main, fontSize: 20, flexShrink: 0 }} />
              </Tooltip>
            )}
            {!collapsed && (
              <>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: theme.palette.text.primary }}>
                  {title}
                </Typography>
                {badge && (
                  <Chip
                    label={badge}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: 9,
                      color: theme.palette.text.secondary,
                      borderColor: theme.palette.divider,
                      backgroundColor: tokens.muted,
                    }}
                  />
                )}
              </>
            )}
          </Box>
          {onToggleCollapsed && (
            <Tooltip title={collapsed ? 'Expand navigation' : 'Collapse navigation'}>
              <Button
                size="small"
                variant="outlined"
                onClick={onToggleCollapsed}
                aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
                sx={{
                  minWidth: 28,
                  width: 28,
                  height: 28,
                  p: 0,
                  position: 'relative',
                  zIndex: 1201,
                  flexShrink: 0,
                  borderColor: theme.palette.divider,
                  borderRadius: `${tokens.radius}px`,
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    borderColor: alpha(theme.palette.primary.main, 0.35),
                    backgroundColor: tokens.hover,
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
              </Button>
            </Tooltip>
          )}
        </Box>
        {!collapsed && description && (
          <Typography sx={{ mt: 0.5, fontSize: 11, color: theme.palette.text.secondary, lineHeight: 1.35 }}>
            {description}
          </Typography>
        )}
      </Box>
      {!collapsed && headerActions && (
        <Box
          data-testid="workspace-sidebar-header-actions"
          sx={{
            px: 1,
            py: 0.55,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: tokens.header,
          }}
        >
          {headerActions}
        </Box>
      )}

      <Box sx={{ p: collapsed ? 0.5 : 1, overflowY: 'auto', flex: 1 }}>
        {isGrouped
          ? items.map((group) => (
            <Box key={group.id} sx={{ mb: collapsed ? 1 : 1.5 }}>
              {!collapsed && (
                <Typography
                  sx={{
                    px: 1,
                    pb: 0.65,
                    fontSize: 10,
                    fontWeight: 800,
                    lineHeight: 1,
                    textTransform: 'uppercase',
                    color: theme.palette.text.secondary,
                  }}
                >
                  {group.label}
                </Typography>
              )}
              {(group.items || []).map(renderNavigationItem)}
            </Box>
          ))
          : items.map(renderNavigationItem)}
      </Box>
      {actions && !collapsed && (
        <Box
          sx={{
            p: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: tokens.header,
          }}
        >
          {actions}
        </Box>
      )}
    </Box>
  );
}

export default WorkspaceSidebar;
